import type { CourtIntelMyVenuesData, VenueData, VenueUpsertRequest, VenuesApiResponse } from "@/types/venue";
import { requestJson } from "@/lib/api-client";

export interface FetchVenuesParams {
  limit?: number;
  offset?: number;
  search?: string;
  lat?: number | string | null;
  lng?: number | string | null;
  mode?: "name" | "location" | null;
}

export async function fetchVenues(params: FetchVenuesParams = {}, signal?: AbortSignal): Promise<VenuesApiResponse> {
  const query: Record<string, string> = {};

  if (typeof params.limit === "number") query.limit = String(params.limit);
  if (typeof params.offset === "number") query.offset = String(params.offset);
  if (params.search) query.search = params.search;
  if (params.lat !== undefined && params.lat !== null) query.lat = String(params.lat);
  if (params.lng !== undefined && params.lng !== null) query.lng = String(params.lng);
  if (params.mode) query.mode = params.mode;

  return requestJson<VenuesApiResponse>("/api/venues", {
    method: "GET",
    query,
    signal,
  });
}

export interface SuggestVenueOptions {
  payload: VenueUpsertRequest;
  newPhotos?: File[];
  deletedPhotos?: string[];
}

export interface SuggestVenueResponse {
  venue: VenueData;
  uploaded?: string[];
}

export async function submitVenueSuggestion(options: SuggestVenueOptions, signal?: AbortSignal): Promise<SuggestVenueResponse> {
  const form = new FormData();
  form.append("payload", JSON.stringify(options.payload));
  if (options.deletedPhotos && options.deletedPhotos.length > 0) {
    form.append("deletedPhotos", JSON.stringify(options.deletedPhotos));
  }

  options.newPhotos?.forEach((file) => {
    form.append("photos", file);
  });

  return requestJson<SuggestVenueResponse>("/api/venues/suggest", {
    method: "POST",
    body: form,
    signal,
  });
}

export interface CheckSimilarVenuesParams {
  name?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CheckSimilarVenuesResponse {
  venues: VenueData[];
}

export async function checkSimilarVenues(params: CheckSimilarVenuesParams, signal?: AbortSignal): Promise<CheckSimilarVenuesResponse> {
  return requestJson<CheckSimilarVenuesResponse>("/api/venues/check-similar", {
    method: "POST",
    body: params,
    signal,
  });
}

export interface PendingVenuesResponse {
  venues: VenueData[];
}

export async function fetchPendingVenues(signal?: AbortSignal): Promise<PendingVenuesResponse> {
  return requestJson<PendingVenuesResponse>("/api/venues/pending", {
    method: "GET",
    signal,
  });
}

export async function fetchVenueById(venueId: number, signal?: AbortSignal): Promise<VenueData> {
  return requestJson<VenueData>(`/api/venues/${venueId}`, {
    method: "GET",
    signal,
  });
}

export async function fetchPublicVenueByCourtId(courtId: number, signal?: AbortSignal): Promise<VenueData> {
  return requestJson<VenueData>("/api/venues/by-court-id", {
    method: "GET",
    query: { court: courtId },
    signal,
  });
}

export async function fetchCourtIntelMyVenues(signal?: AbortSignal): Promise<CourtIntelMyVenuesData> {
  return requestJson<CourtIntelMyVenuesData>("/api/venue-management/my-venues", {
    method: "GET",
    signal,
  });
}

export async function deleteVenue(courtId: number, signal?: AbortSignal): Promise<void> {
  await requestJson<void>(`/api/venues/${courtId}`, {
    method: "DELETE",
    signal,
  });
}
